import { FormLayout as FormLayoutCore, Spacing } from 'ia-react-core';
import { useSitecoreContentQuery } from 'omni-package-react-cms';
import {
  AuthenticationContext,
  ConsultationModeHeader,
  FormContext, FormLayoutProps as FormLayoutPropsCore,
  NeedHelp,
  redirectTo,
  useFooterLinks,
} from 'omni-package-react-core';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import Constants from '~/app/Constants';
import ApplicationWarnings from '~/components/ApplicationWarnings';
import CanQuitGuard from '~/components/CanQuitGuard';
import { setLocationHref } from '~/utils/browserNavigation';

export interface FormLayoutProps extends FormLayoutPropsCore {
  otherApplication?: boolean;
}

const FormLayout: FC<FormLayoutProps> = ({ children, returnPage, canQuit = true, needHelp = false, otherApplication = false, ...props }) => {
  const navigate = useNavigate();
  const { authenticationInfo } = useContext(AuthenticationContext);
  const authValue = authenticationInfo.isAuthenticated ? 'authenticated' : 'non-authenticated';
  const mainFooterLinksUrl = useSitecoreContentQuery(`global/footers/footer-react-${authValue}`, true);
  const footerLinksFromOmniCore = useFooterLinks() ?? [];
  const theme = useContext(ThemeContext);
  const bg = useMemo(() => theme.colors.gbiBlueHaze, [theme.colors.gbiBlueHaze]);

  const handleClose = useCallback(() => {
    if (otherApplication && canQuit) {
      setLocationHref(returnPage);
    } else {
      redirectTo(returnPage, navigate);
    }
  }, [returnPage, navigate, otherApplication, canQuit]);

  const handleMainLogoClick = useCallback((event: React.MouseEvent) => {
    if (otherApplication) {
      event.preventDefault();
    }
  }, [otherApplication]);

  const formContext = useMemo(() => ({
    returnPage,
    logOffPage: Constants.PagesUrl.LogOff,
  }), [returnPage]);

  const location = useLocation();
  const isCentralizationPage = useMemo(() => (
    location.pathname.includes(Constants.PagesUrl.CentralizationBaseFragment)
  ), [location.pathname]);

  return (
    <>
      {!canQuit && <CanQuitGuard />}
      <FormLayoutCore
        p-t={28}
        onClose={handleClose}
        mainFooterLinksUrl={otherApplication ? undefined : mainFooterLinksUrl}
        mainFooterLinks={footerLinksFromOmniCore}
        contentBackground={bg}
        headerBackground="white"
        horizontalLogo
        onMainLogoClick={handleMainLogoClick}
        languageButtonInlineWithSlogan={!!otherApplication}
        {...props}
      >
        <FormLayoutCore.BeforeHeader>
          <ConsultationModeHeader />
        </FormLayoutCore.BeforeHeader>
        <FormLayoutCore.MainHeaderRightContent noColPadding>
          {needHelp && (
            <Spacing m-r-lg={24}>
              <NeedHelp isCentralizedContent={isCentralizationPage} />
            </Spacing>
          )}
        </FormLayoutCore.MainHeaderRightContent>
        <FormLayoutCore.Content>
          <ApplicationWarnings />
          <FormContext.Provider value={formContext}>
            {children}
          </FormContext.Provider>
        </FormLayoutCore.Content>
      </FormLayoutCore>
    </>
  );
};
export default FormLayout;
